/**
 * Confirmation step — terminal screen for both booking and enquiry flows.
 * No further user input expected; just renders a friendly summary and a
 * "Close" button which is wired by the parent modal.
 */

import { formatPence } from '../pricing.js';
import { escapeHTML } from '../utils/formatting.js';
import {
  formatHumanDate,
  formatLongDate,
  fromISODate,
} from '../utils/dates.js';
import type { CreateBookingResponse } from '../api.js';
import type { CalendarState } from './Calendar.js';
import type { GuestFormState } from './GuestForm.js';

export interface ConfirmationOptions {
  flow: 'booking' | 'enquiry';
  result: CreateBookingResponse;
  calendar: CalendarState;
  guest: GuestFormState;
}

export class ConfirmationStep {
  private readonly host: HTMLElement;

  constructor(host: HTMLElement, opts: ConfirmationOptions) {
    this.host = host;
    this.render(opts);
  }

  destroy(): void {
    this.host.innerHTML = '';
  }

  private render(opts: ConfirmationOptions): void {
    try {
      this.renderInner(opts);
    } catch {
      // Absolute last-ditch fallback so the user still sees a reference.
      this.renderMinimalFallback(opts);
    }
  }

  private renderMinimalFallback(opts: ConfirmationOptions): void {
    const ref = escapeHTML(opts.result?.reference ?? '');
    this.host.innerHTML = `
      <div class="no4w-confirmation">
        <div class="no4w-confirmation-icon" aria-hidden="true">✓</div>
        <h2>Booking confirmed</h2>
        <div class="no4w-confirmation-reference">Ref: ${ref}</div>
        <p>Booking confirmed — check your email — Ref: ${ref}</p>
      </div>
    `;
  }

  private renderInner(opts: ConfirmationOptions): void {
    const { flow, result, calendar, guest } = opts;
    const name = escapeHTML(guest.firstName || 'there');
    const ref = escapeHTML(result.reference);

    if (flow === 'enquiry') {
      this.host.innerHTML = `
        <div class="no4w-confirmation">
          <div class="no4w-confirmation-icon" aria-hidden="true">✓</div>
          <h2>Enquiry sent</h2>
          <div class="no4w-confirmation-reference">Ref: ${ref}</div>
          <p>
            Thank you, ${name}! We've received your enquiry and will
            be in touch shortly.
          </p>
          <div class="no4w-confirmation-details">
            <p><strong>Dates:</strong> ${
              calendar.checkIn ? escapeHTML(formatHumanDate(calendar.checkIn)) : '—'
            } → ${calendar.checkOut ? escapeHTML(formatHumanDate(calendar.checkOut)) : '—'}</p>
            <p><strong>Email:</strong> ${escapeHTML(guest.email)}</p>
            <p><strong>Phone:</strong> ${escapeHTML(guest.phone)}</p>
          </div>
          <p class="no4w-hint">
            A copy of this enquiry has been sent to ${escapeHTML(guest.email)}.
          </p>
        </div>
      `;
      return;
    }

    const price = result.priceBreakdown;
    let balanceDue = '';
    if (price?.balanceDueDate) {
      const parsed = fromISODate(price.balanceDueDate);
      if (parsed) balanceDue = formatLongDate(parsed);
    }

    // If both check-in and check-out are missing/bad, show the minimal fallback —
    // there's not enough intact data to render a useful confirmation.
    if (!calendar.checkIn && !calendar.checkOut) {
      this.renderMinimalFallback(opts);
      return;
    }

    this.host.innerHTML = `
      <div class="no4w-confirmation">
        <div class="no4w-confirmation-icon" aria-hidden="true">✓</div>
        <h2>Booking confirmed</h2>
        <div class="no4w-confirmation-reference">Ref: ${ref}</div>
        <p>
          Thank you, ${name}! Your stay at No. 4 High Terrace is confirmed.
        </p>
        <div class="no4w-confirmation-details">
          <p>
            <strong>Check-in:</strong>
            ${calendar.checkIn ? escapeHTML(formatHumanDate(calendar.checkIn)) : '—'}
          </p>
          <p>
            <strong>Check-out:</strong>
            ${calendar.checkOut ? escapeHTML(formatHumanDate(calendar.checkOut)) : '—'}
          </p>
          <p>
            <strong>Deposit paid:</strong> ${escapeHTML(formatPence(price.depositPence))}
          </p>
          <p>
            <strong>Balance due:</strong> ${escapeHTML(formatPence(price.balancePence))}
            ${balanceDue ? `by ${escapeHTML(balanceDue)}` : ''}
          </p>
        </div>
        <p>
          You'll receive check-in instructions by email 5 days before
          your arrival. If you have any questions, please get in touch.
        </p>
        <p class="no4w-hint">
          A confirmation email is on its way to ${escapeHTML(guest.email)} —
          please check your inbox in a few minutes (and your spam folder just in case).
        </p>
      </div>
    `;
  }
}
